import { Dictionary, SafeDictionary } from "@insite/client-framework/Common/Types";
import deepFreezeObject from "@insite/client-framework/Common/Utilities/deepFreezeObject";
import { ProductsState } from "@insite/client-framework/Store/Data/Products/ProductsState";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import AnalyticsState from "@store/Store/Data/Analytics/AnalyticsState";
import { CreditInformationsState } from "@store/Store/Data/CreditInformations/CreditInformationsState";
import { HideAllPricesModeState } from "@store/Store/Data/HideAllPricesMode/HideAllPricesModeState";
import { PartsState } from "@store/Store/Data/Parts/PartsState";
import { PaymentsState } from "@store/Store/Data/Payment/PaymentsState";
import { BulkAddToCartState } from "@store/Store/Data/QuickOrder/BulkAddToCartState";
import { QuoteToCartState } from "@store/Store/Data/QuoteToCart/QuoteToCartState";
import { RetailModeState } from "@store/Store/Data/RetailMode/RetailModeState";
import { RetailModeSettingsState } from "@store/Store/Data/RetailModeSettings/RetailModeSettingsState";
import { SpecialPromotionsState } from "@store/Store/Data/SpecialPromotions/SpecialPromotionsState";
import { Draft } from "immer";
import assign from "lodash/assign"
import sortBy from "lodash/sortBy"

export interface DataView {
    readonly fetchedDate: Date;
    readonly ids?: string[];
    readonly isLoading: boolean;
    readonly pagination?: Readonly<PaginationModel>;
    readonly properties: Dictionary<string>;
}

interface HasById<Model> {
    readonly isLoading: Dictionary<boolean>;
    readonly byId: Dictionary<Readonly<Model>>;
    readonly errorStatusCodeById?: Dictionary<Readonly<number>>;
}

export interface BaseDataViewState<Model, DataViewModel extends DataView = DataView> extends HasById<Model> {
    readonly dataViews: Dictionary<DataViewModel>;
}

export default interface DataState {
    readonly analytics: AnalyticsState;
    readonly retailModeSettings: RetailModeSettingsState;
    readonly retailMode: RetailModeState;
    readonly hideAllPricesMode: HideAllPricesModeState;
    readonly parts: PartsState;
    readonly quoteToCart: QuoteToCartState;
    readonly payments: PaymentsState;
    readonly creditInformations: CreditInformationsState;
    readonly bulkAddToCart: BulkAddToCartState;
	readonly products: ProductsState;
	readonly specialPromotions: SpecialPromotionsState;
}

export function setBaseDataViewLoading<T>(dataViewState: BaseDataViewState<T>, parameter: object) {
    dataViewState.dataViews[getDataViewKey(parameter)] = {
        isLoading: true,
        ids: undefined,
        fetchedDate: new Date(),
        properties: {},
    };
}
export function setBaseDataViewLoaded<T>(
    draft: Draft<BaseDataViewState<T>>,
    parameter: object,
    collection: { pagination?: PaginationModel | null; properties?: Dictionary<string> },
    extraData?: DataView,
) {
    draft.dataViews[getDataViewKey(parameter)] = {
        isLoading: false,
        pagination: collection.pagination ? collection.pagination : undefined,
        properties: collection.properties ? collection.properties : {},
        fetchedDate: new Date(),
        ...extraData,
    };
}

export function getBaseDataView<T, DataViewModel extends DataView>(
    dataViewState: BaseDataViewState<T, DataViewModel>,
    parameter: object | undefined,
) {
    return dataViewState.dataViews[getDataViewKey(parameter)];
}

export function assignById<T extends { id: string }>(draft: Draft<BaseDataViewState<T>>, value: T) {
    const existingValue = draft.byId[value.id] ?? {};
    assign(existingValue, value);
    draft.byId[value.id] = existingValue;
}

export function setDataViewLoading<T extends { id: string }>(dataViewState: BaseDataViewState<T>, parameter: object) {
    dataViewState.dataViews[getDataViewKey(parameter)] = {
        isLoading: true,
        ids: undefined,
        fetchedDate: new Date(),
        properties: {},
    };
}

export function setDataViewLoaded<
    T extends { id: string },
    DataViewModel extends DataView,
    CollectionType extends { pagination?: PaginationModel | null; properties?: Dictionary<string> },
>(
    draft: Draft<BaseDataViewState<T>>,
    parameter: object,
    collection: CollectionType,
    getModels: (collection: CollectionType) => T[],
    modelAction?: (value: T) => void,
    addExtraData?: (dataView: DataViewModel) => void,
) {
    const ids: string[] = [];
    const values = getModels(collection);
    for (const value of values) {
        ids.push(value.id);
        if (modelAction) {
            modelAction(value);
        }
        assignById(draft, value);
    }

    const dataView: DataView = {
        isLoading: false,
        ids,
        pagination: collection.pagination ? collection.pagination : undefined,
        properties: collection.properties ? collection.properties : {},
        fetchedDate: new Date(),
    };
    if (addExtraData) {
        addExtraData(dataView as DataViewModel);
    }

    draft.dataViews[getDataViewKey(parameter)] = dataView;
}

export const dataViewNotFound = Object.freeze({
    isLoading: false,
    value: undefined,
} as const);

export function getDataView<T extends { id: string }, DataViewModel extends DataView>(
    dataViewState: BaseDataViewState<T, DataViewModel>,
    parameter: object | undefined,
) {
    const key = getDataViewKey(parameter);
    const dataView = dataViewState.dataViews[key];
    if (!dataView) {
        return dataViewNotFound;
    }

    return getOrStoreCachedResult(dataViewState, key, () => {
        let models: T[] | undefined;
        const { ids, ...otherProps } = dataView;
        if (ids) {
            models = [];
            for (const id of ids) {
                models.push(dataViewState.byId[id]);
            }
        }

        return {
            value: models,
            ...otherProps,
        };
    });
}

export function replaceDataView<T extends { id: string }, DataViewModel extends DataView>(
    draft: Draft<BaseDataViewState<T>>,
    parameter: object,
    dataView: object,
) {
    draft.dataViews[getDataViewKey(parameter)] = dataView as DataViewModel;
}

const cacheMap = new WeakMap<object, SafeDictionary<any>>();

function getOrStoreCachedResult<ModelType, ResultType extends object>(
    dataViewState: HasById<ModelType>,
    key: string,
    createResult: () => ResultType,
) {
    const cached = cacheMap.get(dataViewState)?.[key];
    if (cached) {
        return cached as Readonly<ResultType>;
    }

    const result = deepFreezeObject(createResult());
    let dictionary = cacheMap.get(dataViewState);
    if (!dictionary) {
        dictionary = {};
        cacheMap.set(dataViewState, dictionary);
    }

    dictionary[key] = result;
    return result;
}

export function getDataViewKey(parameter?: object | undefined) {
    if (!parameter) {
        return "EMPTY_KEY";
    }
    let dataViewKey = "";
    const keys = sortBy(Object.keys(parameter), o => o);
    for (const key of keys) {
        // eslint-disable-next-line no-prototype-builtins
        if (parameter.hasOwnProperty(key)) {
            const value = (parameter as Dictionary<string>)[key];
            const type = typeof value;
            if (type === "undefined" || type === "function") {
                continue;
            }
            const stringValue = value.toString();
            if (key === "page" && stringValue === "1") {
                continue;
            }
            dataViewKey += `${key}=${stringValue}&`;
        }
    }

    dataViewKey = dataViewKey.length > 0 ? dataViewKey.substr(0, dataViewKey.length - 1) : dataViewKey;

    return dataViewKey === "" ? "EMPTY_KEY" : dataViewKey;
}